import { useMemo } from 'react';
import { LogbookActor } from './LogbookActor';
import { actionMeta, formatCount, relativeTime } from '../../utils/logbook';
import type { LogbookPresence } from '../../utils/logbookNearby';
import type { LogbookEvent } from '../../types/logbook';

interface LogbookNearbyMapProps {
  events: LogbookEvent[];
  /** Ranked presence, so the most present actors get the distinct colours. */
  presence: LogbookPresence[];
  centre: { x: number; y: number; z: number };
  radius: number;
  className?: string;
}

const PALETTE = [
  'var(--accent-300)',
  'var(--tone-purple-text)',
  'var(--tone-success-text)',
  'var(--tone-amber-text)',
  'var(--tone-orange-text)',
  'var(--tone-info-text)',
];
const OTHERS = 'var(--tone-neutral-text)';
const SIZE = 100;

/**
 * A top-down plot of where the loaded records fell around the position.
 *
 * North is up, as on an in-game map, so a moderator standing at the coordinates can turn the
 * picture into a direction. Each dot is one record and each colour one actor, which is enough to
 * see a path, a cluster around a chest, or one person circling a build while everyone else passed
 * through. It draws the loaded page only, and says so: a plot cannot count, the tiles above do.
 */
export const LogbookNearbyMap = ({ events, presence, centre, radius, className }: LogbookNearbyMapProps) => {
  const colours = useMemo(() => {
    const byActor = new Map<string, string>();
    presence.slice(0, PALETTE.length).forEach((entry, index) => byActor.set(entry.actorId, PALETTE[index]));
    return byActor;
  }, [presence]);

  const points = useMemo(() => {
    const scale = SIZE / 2 / Math.max(radius, 1);
    return events
      .map((event) => ({
        event,
        cx: SIZE / 2 + (event.x - centre.x) * scale,
        cy: SIZE / 2 + (event.z - centre.z) * scale,
        colour: colours.get(event.actor_id) ?? OTHERS,
      }))
      .filter((point) => point.cx >= 0 && point.cx <= SIZE && point.cy >= 0 && point.cy <= SIZE);
  }, [events, centre, radius, colours]);

  const legend = presence.slice(0, PALETTE.length);
  const others = presence.length - legend.length;

  if (events.length === 0) {
    return <p className={className ? `${className} text-sm text-glass-500` : 'text-sm text-glass-500'}>Nothing to plot yet.</p>;
  }

  return (
    <div className={className}>
      <div className="relative w-full max-w-[420px] mx-auto">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-full h-auto rounded-lg border border-glass-200/10 bg-glass-200/[0.03]"
          role="img"
          aria-label={`${formatCount(points.length)} records plotted around ${centre.x}, ${centre.z}, north up`}
        >
          {/* Quarter and half radius rings, so distance reads without a scale bar */}
          {[0.25, 0.5, 0.75].map((fraction) => (
            <rect
              key={fraction}
              x={SIZE / 2 - (SIZE / 2) * fraction}
              y={SIZE / 2 - (SIZE / 2) * fraction}
              width={SIZE * fraction}
              height={SIZE * fraction}
              fill="none"
              stroke="currentColor"
              strokeWidth={0.25}
              className="text-glass-200/20"
            />
          ))}
          <line x1={SIZE / 2} y1={0} x2={SIZE / 2} y2={SIZE} stroke="currentColor" strokeWidth={0.25} className="text-glass-200/25" />
          <line x1={0} y1={SIZE / 2} x2={SIZE} y2={SIZE / 2} stroke="currentColor" strokeWidth={0.25} className="text-glass-200/25" />

          {points.map(({ event, cx, cy, colour }) => (
            <circle key={event.event_id} cx={cx} cy={cy} r={1.4} fill={colour} fillOpacity={0.85}>
              <title>
                {`${event.actor_name || (event.actor_kind === 'unknown' ? 'Unattributed' : event.actor_kind)}: ${actionMeta(event.action).label} at ${Math.round(event.x)}, ${Math.round(event.y)}, ${Math.round(event.z)}${event.timestamp > 0 ? `, ${relativeTime(event.timestamp)}` : ''}`}
              </title>
            </circle>
          ))}

          {/* The position itself */}
          <circle cx={SIZE / 2} cy={SIZE / 2} r={2.2} fill="none" stroke="white" strokeWidth={0.6} />
          <circle cx={SIZE / 2} cy={SIZE / 2} r={0.6} fill="white" />
        </svg>
        <span className="absolute top-1.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-glass-400 select-none">N</span>
        <span className="absolute bottom-1.5 right-2 text-[10px] tabular-nums text-glass-500 select-none">{radius} blocks to the edge</span>
      </div>

      <ul className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        {legend.map((entry) => (
          <li key={entry.actorId || 'unattributed'} className="inline-flex items-center gap-1.5 min-w-0">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colours.get(entry.actorId) }} aria-hidden />
            <LogbookActor actorId={entry.actorId} actorName={entry.actorName} actorKind={entry.actorKind} />
          </li>
        ))}
        {others > 0 && (
          <li className="inline-flex items-center gap-1.5 text-glass-500">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: OTHERS }} aria-hidden />
            {formatCount(others)} others
          </li>
        )}
      </ul>
    </div>
  );
};
